import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const HomeLeft = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Add cache busting parameter to avoid cached errors
      const apiUrl = `https://backend.dlapakistan.com/api/cards/?_=${Date.now()}`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        // Include credentials if the API requires authentication
        credentials: 'omit',
        mode: 'cors',
      });
      
      if (!response.ok) {
        // Try to get error details from response
        let errorDetails = '';
        try {
          const errorData = await response.text();
          errorDetails = errorData.substring(0, 100); // First 100 chars
        } catch (e) {
          // Ignore parsing error
        }
        
        throw new Error(`Server error ${response.status}: ${response.statusText}. ${errorDetails}`);
      }
      
      // Get response text first to inspect it
      const responseText = await response.text();
      
      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON:', responseText.substring(0, 200));
        throw new Error('Invalid JSON response from server');
      }
      
      // Extract cards array from various possible response formats
      let cardsArray = [];
      
      if (Array.isArray(data)) {
        // Case 1: Direct array
        cardsArray = data;
      } 
      else if (data && typeof data === 'object') {
        // Case 2: Object with results array (Django REST Framework pagination)
        if (data.results && Array.isArray(data.results)) {
          cardsArray = data.results;
        }
        // Case 3: Object with data array
        else if (data.data && Array.isArray(data.data)) {
          cardsArray = data.data;
        }
        // Case 4: Object with cards array
        else if (data.cards && Array.isArray(data.cards)) {
          cardsArray = data.cards;
        }
        // Case 5: Object with items array
        else if (data.items && Array.isArray(data.items)) {
          cardsArray = data.items;
        }
        // Case 6: Check if any property contains an array
        else {
          // Find any array property
          for (const key in data) {
            if (Array.isArray(data[key]) && data[key].length > 0) {
              // Check if first item looks like a card
              const firstItem = data[key][0];
              if (firstItem && typeof firstItem === 'object' && (firstItem.title || firstItem.id)) {
                cardsArray = data[key];
                break;
              }
            }
          }
          
          // If still no array found, try Object.values
          if (cardsArray.length === 0) {
            const values = Object.values(data);
            if (values.length > 0) {
              // Check if values themselves are cards
              if (values.every(item => item && typeof item === 'object' && (item.title || item.id))) {
                cardsArray = values;
              }
            }
          }
        }
      }
      
      // Final check: ensure we have an array
      if (!Array.isArray(cardsArray) || cardsArray.length === 0) {
        console.warn('No cards array found in response:', data);
        // Don't throw error, just set empty array
        setCards([]);
        setLoading(false);
        return;
      }
      
      // Process cards to fix image URLs
      const processedCards = cardsArray
        .map(card => {
          // Skip if card is not valid
          if (!card || typeof card !== 'object') {
            return null;
          }
          
          // Ensure card has an id
          const processedCard = { ...card };
          if (!processedCard.id) {
            processedCard.id = `card-${Math.random().toString(36).substr(2, 9)}`;
          }
          
          // Handle image URL
          if (!processedCard.image_url && processedCard.image) {
            // If image is a relative path, prepend the server URL
            if (processedCard.image.startsWith('/')) {
              processedCard.image_url = `https://backend.dlapakistan.com${processedCard.image}`;
            }
            // If image is just a filename
            else if (!processedCard.image.startsWith('http')) {
              processedCard.image_url = `https://backend.dlapakistan.com/media/cards/${processedCard.image}`;
            } else {
              processedCard.image_url = processedCard.image;
            }
          }
          
          // If image_url exists but is relative, make it absolute
          if (processedCard.image_url && !processedCard.image_url.startsWith('http')) {
            if (processedCard.image_url.startsWith('/')) {
              processedCard.image_url = `https://backend.dlapakistan.com${processedCard.image_url}`;
            } else {
              processedCard.image_url = `https://backend.dlapakistan.com/${processedCard.image_url}`;
            }
          }
          
          return processedCard;
        })
        .filter(card => card !== null); // Remove any null entries
      
      setCards(processedCards);
      
    } catch (err) {
      console.error('Fetch error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      
      // Set user-friendly error message
      let userMessage = err.message;
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        userMessage = 'Network error: Unable to connect to the server. Please check your internet connection or try again later.';
      } else if (err.message.includes('CORS')) {
        userMessage = 'CORS error: The server is not configured to allow requests from this domain.';
      }
      
      setError(`Cannot fetch cards: ${userMessage}`);
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = (card, index) => {
    if (card.title && (card.title.includes("Dental Technology Show") || index === 0)) {
      navigate("/dts-show");
    } else {
      // Handle other card clicks if needed
      console.log('Card clicked:', card.title);
    }
  };

  // Helper function to get image source with fallback
  const getImageSrc = (card) => {
    return card.image_url || null;
  };

  // Loading state
  if (loading) {
    return (
      <div className="px-4 py-10 mb-32 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className="bg-white shadow-sm overflow-hidden flex flex-col animate-pulse"
              style={{ borderRadius: '0' }}
            >
              <div className="h-44 bg-gray-300"></div>
              <div className="p-4 flex flex-col flex-grow">
                <div className="h-5 bg-gray-300 mb-3 w-3/4"></div>
                <div className="h-3 bg-gray-300 mb-4 w-full"></div>
                <div className="h-3 bg-gray-300 mb-4 w-2/3"></div>
                <div className="mt-auto w-full py-2 bg-gray-300"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="px-4 py-10 mb-32 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center py-10 bg-red-50 border border-red-200 p-8">
          <p className="text-red-600 mb-4 text-sm">{error}</p>
          <button
            onClick={fetchCards}
            className="px-5 py-1.5 bg-[#70B444] hover:bg-[#5a9c38] text-white text-sm transition"
            style={{ borderRadius: '0' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (cards.length === 0) {
    return (
      <div className="px-4 py-10 mb-32 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center py-16 bg-gray-50">
          <p className="text-gray-500 text-base mb-4">No cards available</p>
          <button
            onClick={fetchCards}
            className="px-5 py-1.5 bg-[#70B444] hover:bg-[#5a9c38] text-white text-sm transition"
            style={{ borderRadius: '0' }}
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  // Success state with cards
  return (
    <div className="px-4 py-8 mb-24 md:px-6 lg:px-8 max-w-6xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card, index) => {
          const imageSrc = getImageSrc(card);
          const cardId = card.id || `card-${index}`;
          
          return (
            <div
              key={cardId}
              className="bg-white shadow-sm overflow-hidden flex flex-col hover:shadow-md transition"
              style={{ borderRadius: '0' }}
            >
              <div className="h-44 overflow-hidden flex items-center justify-center bg-gray-100">
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt={card.title || 'Card image'}
                    className="max-h-full max-w-full object-contain"
                    style={{ 
                      borderRadius: '0',
                      width: 'auto',
                      height: 'auto',
                      maxHeight: '100%',
                      maxWidth: '100%'
                    }}
                    onError={(e) => {
                      console.log('Image failed to load:', imageSrc);
                      e.target.onerror = null; // Prevent infinite loop
                      e.target.style.display = 'none';
                      
                      // Create fallback text
                      const container = e.target.parentElement;
                      if (container && !container.querySelector('.fallback-text')) {
                        const fallback = document.createElement('div');
                        fallback.className = 'fallback-text flex items-center justify-center h-full w-full';
                        fallback.innerHTML = '<span class="text-gray-500 text-sm">Image not available</span>';
                        container.appendChild(fallback);
                      }
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full w-full">
                    <span className="text-gray-500 text-sm">No image available</span>
                  </div>
                )}
              </div>

              <div className="p-4 flex flex-col flex-grow">
                <h3 className="text-base font-medium text-gray-900 mb-1.5 line-clamp-2">
                  {card.title || 'Untitled'}
                </h3>

                {card.subtitle && card.subtitle.trim() !== "" && (
                  <p className="text-xs text-gray-600 mb-4 line-clamp-2">
                    {card.subtitle}
                  </p>
                )}

                <button
                  onClick={() => handleButtonClick(card, index)}
                  className="mt-auto w-full py-2 bg-[#70B444] hover:bg-[#5a9c38] text-white text-sm transition"
                  style={{ borderRadius: '0' }}
                >
                  Find out more
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HomeLeft;